import {
  getUploadIdentity,
  isMediaUploadEnabled,
  mintUuid,
  sendSignedRequest,
  signS3Put,
} from '@muzly/api-client';
import type { HttpError, SignedS3Put } from '@muzly/api-client';
import { MEDIA_PREFIX } from '@muzly/shared';

import type { AnalyStereo } from './analyContract';

/**
 * mediaUpload — 테이크 파일 하나를 S3 에 올리고 `POST /medias/0` 으로 등록해 `mediaId` 를
 * 받는다. 두 요청을 **한 단위로** 묶는 이유: 반쪽 성공(파일은 있는데 레코드가 없다, 또는
 * 레코드는 있는데 파일이 없다)을 호출부로 흘리면 그 상태를 판정할 자리가 어디에도 없다.
 *
 * 계약은 muzly-wiki PR #52(백엔드 인계본 정정판) · 서버 SPEC D29 다. presign 은 없다 — muzly
 * 에는 로그인이 없고, `register-device` 가 돌려주는 임시 크리덴셜(`Token.credential`)로 직접
 * PUT 한 뒤 서버에는 **주소만** 등록한다. 서버는 파일을 열지도 검증하지도 않으므로 값의
 * 정확성은 이쪽 책임이다.
 *
 * 실패는 전부 `null` 이다. 업로드가 안 되는 것은 진단 실패가 아니다 — 점수는 오디오가
 * 서버로 가는 것과 무관하게 이미 나와 있고, 호출부는 `mediaId` 없이 마감하면 된다.
 */

/** 등록 바디. 서버가 받는 필드 중 우리가 채우는 것만(`MediaTransformer.bodyToModel`). */
export interface MediaRegisterBody {
  /** LUT 키다. 오탈자는 400 — analy 의 `stereo`(`song`·`sustain`…)와 다른 축이다. */
  stereo: 'audio';
  name: string;
  contentType: string;
  /** **바이트 길이**. base64 문자열 길이를 넣으면 레코드가 조용히 4/3 배로 틀린다. */
  contentSize: number;
  bucket: string;
  key: string;
  url: string;
}

export interface UploadTakeInput {
  /** 어느 세그먼트의 테이크인가 — 파일 이름에만 쓴다. */
  stereo: AnalyStereo;
  body: Uint8Array;
}

export interface MediaUploadDeps {
  identity: () => Promise<string | null>;
  sign: (input: {
    key: string;
    contentType: string;
    body: Uint8Array;
  }) => Promise<SignedS3Put | null>;
  put: (signed: SignedS3Put, body: Uint8Array) => Promise<boolean>;
  /** `retryable` 은 "다시 보내면 답이 달라질 수 있나"다 — 5xx·무응답만 참이다. */
  register: (
    body: MediaRegisterBody,
  ) => Promise<{ id: string | null; retryable: boolean }>;
  uuid: () => string;
  prefix: string;
}

const CONTENT_TYPE = 'audio/wav';

/**
 * 업로드 한 건의 예산. 회차 마감이 기다려 주는 시간과 **같은 값이어야 한다** — 마감이 포기한
 * 뒤에도 PUT 이 계속 돌면 그 바이트(최대 4MB)가 네트워크가 끝날 때까지 힙에 남는다.
 * `useRecordMachine` 이 이 상수를 그대로 쓴다.
 */
export const UPLOAD_TIMEOUT_MS = 15_000;

/**
 * S3 로 바이트를 올린다. 서명된 헤더는 **그대로** 실어야 한다 — 하나라도 빼거나 더하면
 * 서명이 어긋나 403 이다. `host` 는 브라우저가 조용히 버리는데(forbidden header) 무해하다:
 * 실제 Host 는 URL 이 정한다.
 */
const putObject = async (
  signed: SignedS3Put,
  body: Uint8Array,
): Promise<boolean> => {
  try {
    const res = await fetch(signed.url, {
      method: 'PUT',
      headers: signed.headers,
      body: body as BodyInit,
      // 마감이 포기하는 시점에 이 요청도 같이 끊는다. 안 끊으면 아무도 결과를 안 읽는
      // 전송이 계속 돌면서 바이트를 붙잡는다.
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });
    return res.ok;
  } catch {
    return false;
  }
};

/**
 * `POST /medias/0` — `id` 는 리터럴 `0` 이어야 한다(다른 값은 `@id (string) is invalid!`).
 *
 * 실패를 상태코드로 갈라 돌려준다. 4xx 는 바디가 틀린 것이라 같은 바디를 다시 보내도
 * 같은 답이 온다 — 재시도는 서버에 나쁜 요청을 한 번 더 보내는 일일 뿐이다.
 */
const registerMedia = async (
  body: MediaRegisterBody,
): Promise<{ id: string | null; retryable: boolean }> => {
  try {
    const view = await sendSignedRequest<{ id?: string }>({
      method: 'POST',
      path: '/medias/0',
      body,
    });
    return { id: view.id ?? null, retryable: false };
  } catch (error) {
    const status = (error as HttpError).status;
    return { id: null, retryable: status === undefined || status >= 500 };
  }
};

const defaultDeps = (): MediaUploadDeps => ({
  identity: getUploadIdentity,
  sign: signS3Put,
  put: putObject,
  register: registerMedia,
  // `crypto.randomUUID` 는 secure context 밖에 없다 — Android 에뮬레이터 WebView
  // (`http://10.0.2.2:4700`)가 바로 그 환경이고, 하필 네이티브 업로드를 검증하는 자리다.
  uuid: mintUuid,
  prefix: MEDIA_PREFIX,
});

export const uploadTake = async (
  input: UploadTakeInput,
  deps: MediaUploadDeps = defaultDeps(),
): Promise<string | null> => {
  // 업로드가 꺼져 있으면(버킷·프리픽스 중 하나라도 빈 값) 여기서 끝난다. 호출부도 같은
  // 판정을 하지만(파일을 읽기 전에 막아야 하므로) 이 함수 단독으로도 안전해야 한다.
  if (!isMediaUploadEnabled()) {
    return null;
  }
  // 키 프리픽스는 취향이 아니라 IAM 조건이다:
  // `medias/<stage>/${cognito-identity.amazonaws.com:sub}/*`. 신원을 모르면 만들 수 있는
  // 주소가 없다 — 아무 데나 올려 보는 것은 403 을 한 번 더 받는 일일 뿐이다.
  const identityId = await deps.identity();
  if (identityId === null) {
    return null;
  }

  const key = `${deps.prefix}/${identityId}/${deps.uuid()}.wav`;
  const signed = await deps.sign({
    key,
    contentType: CONTENT_TYPE,
    body: input.body,
  });
  if (signed === null) {
    return null;
  }
  // 바이트 길이를 **여기서** 뽑아 둔다 — 등록 바디가 `input.body` 를 참조하면 PUT 이 끝난
  // 뒤 등록 왕복(최대 2회) 동안 그 수 MB 가 계속 살아 있다.
  const contentSize = input.body.byteLength;
  if (!(await deps.put(signed, input.body))) {
    return null;
  }

  const registerBody: MediaRegisterBody = {
    stereo: 'audio',
    name: `take-${input.stereo}.wav`,
    contentType: CONTENT_TYPE,
    contentSize,
    // 서명이 돌려준 버킷을 쓴다 — 여기서 env 를 다시 읽으면 PUT 대상과 갈릴 수 있다.
    bucket: signed.bucket,
    key,
    url: signed.url,
  };
  // 등록만 실패하면 S3 에 파일이 남는다 — 라이프사이클 규칙이 없어 영구히 남는 고아다.
  // 지울 권한도 없다(role 은 `PutObject` 뿐). 그래서 **다시 보낼 값어치가 있을 때만** 한 번
  // 더 시도한다: 여기서 성공하면 고아가 안 생기고, 4xx 면 다시 보내도 같은 답이다.
  // `registerMedia` 는 성공에 `retryable: false` 를 실으므로 이 한 줄이 성공·영구실패를
  // 같이 걷어낸다.
  const first = await deps.register(registerBody);
  if (!first.retryable) {
    return first.id;
  }
  return (await deps.register(registerBody)).id;
};
